import { useState, useMemo } from 'react';
import { Plus, Search, Filter, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { useTasks } from '../hooks/useTasks';
import { TaskCard } from '../components/TaskCard';
import { TaskModal } from '../components/TaskModal';
import { Button } from '../components/Button';
import { cn } from '../utils/cn';

const filters = [
    { value: 'all', label: 'All Tasks', icon: null },
    { value: 'active', label: 'Active', icon: Clock },
    { value: 'completed', label: 'Completed', icon: CheckCircle2 },
    { value: 'overdue', label: 'Overdue', icon: AlertCircle },
];

export function Dashboard() {
    const { tasks, loading, createTask, updateTask, deleteTask, toggleTaskComplete } = useTasks();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');

    const filteredTasks = useMemo(() => {
        let result = tasks;

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(
                task =>
                    task.title.toLowerCase().includes(query) ||
                    task.description?.toLowerCase().includes(query) ||
                    task.category?.toLowerCase().includes(query)
            );
        }

        // Apply status filter
        switch (activeFilter) {
            case 'active':
                result = result.filter(task => !task.completed);
                break;
            case 'completed':
                result = result.filter(task => task.completed);
                break;
            case 'overdue':
                result = result.filter(
                    task =>
                        !task.completed &&
                        task.dueDate &&
                        new Date(task.dueDate) < new Date()
                );
                break;
            default:
                break;
        }

        // Sort by due date (overdue first), then by creation date
        return result.sort((a, b) => {
            if (a.completed !== b.completed) {
                return a.completed ? 1 : -1;
            }
            if (a.dueDate && b.dueDate) {
                return new Date(a.dueDate) - new Date(b.dueDate);
            }
            return new Date(b.createdAt) - new Date(a.createdAt);
        });
    }, [tasks, searchQuery, activeFilter]);

    const stats = useMemo(() => {
        const total = tasks.length;
        const completed = tasks.filter(t => t.completed).length;
        const overdue = tasks.filter(
            t => !t.completed && t.dueDate && new Date(t.dueDate) < new Date()
        ).length;
        const active = total - completed;

        return { total, completed, overdue, active };
    }, [tasks]);

    const handleCreateTask = async (taskData) => {
        await createTask(taskData);
    };

    const handleUpdateTask = async (taskData) => {
        if (editingTask) {
            await updateTask(editingTask.id, taskData);
            setEditingTask(null);
        }
    };

    const handleEdit = (task) => {
        setEditingTask(task);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingTask(null);
    };

    const handleSubmit = (taskData) => {
        if (editingTask) {
            handleUpdateTask(taskData);
        } else {
            handleCreateTask(taskData);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    {[
                        { label: 'Total Tasks', value: stats.total, color: 'bg-blue-500' },
                        { label: 'Active', value: stats.active, color: 'bg-yellow-500' },
                        { label: 'Completed', value: stats.completed, color: 'bg-green-500' },
                        { label: 'Overdue', value: stats.overdue, color: 'bg-red-500' },
                    ].map((stat) => (
                        <div key={stat.label} className="bg-white rounded-xl shadow-sm p-4">
                            <div className="flex items-center gap-3">
                                <div className={cn('w-3 h-3 rounded-full', stat.color)} />
                                <div>
                                    <p className="text-sm text-gray-600">{stat.label}</p>
                                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">My Tasks</h1>
                        <p className="text-gray-600 mt-1">
                            Manage your tasks and stay organized
                        </p>
                    </div>
                    <Button
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center gap-2"
                    >
                        <Plus className="w-5 h-5" />
                        New Task
                    </Button>
                </div>

                {/* Filters and Search */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search tasks..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={cn(
                                'w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg',
                                'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                                'outline-none transition-all duration-200'
                            )}
                        />
                    </div>
                    <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0">
                        <Filter className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        {filters.map((filter) => {
                            const Icon = filter.icon;
                            return (
                                <button
                                    key={filter.value}
                                    onClick={() => setActiveFilter(filter.value)}
                                    className={cn(
                                        'flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors',
                                        activeFilter === filter.value
                                            ? 'bg-primary-100 text-primary-700'
                                            : 'bg-white text-gray-600 hover:bg-gray-100'
                                    )}
                                >
                                    {Icon && <Icon className="w-4 h-4" />}
                                    {filter.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tasks List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto mb-4" />
                        <p className="text-gray-600">Loading tasks...</p>
                    </div>
                ) : filteredTasks.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <CheckCircle2 className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {tasks.length === 0 ? 'No tasks yet' : 'No tasks found'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {tasks.length === 0
                                ? 'Create your first task to get started'
                                : 'Try adjusting your search or filters'}
                        </p>
                        {tasks.length === 0 && (
                            <Button onClick={() => setIsModalOpen(true)}>
                                Create First Task
                            </Button>
                        )}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {filteredTasks.map((task) => (
                            <TaskCard
                                key={task.id}
                                task={task}
                                onToggleComplete={toggleTaskComplete}
                                onEdit={handleEdit}
                                onDelete={deleteTask}
                            />
                        ))}
                    </div>
                )}
            </div>

            <TaskModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleSubmit}
                task={editingTask}
            />
        </div>
    );
}